import { Attribution } from "../types/common";

export type CityImageCacheEntry = {
  urls: string[];
  attributions: (Attribution | null)[];
};

const cache = new Map<string, CityImageCacheEntry>();
const loading = new Map<string, { count: number; promise: Promise<CityImageCacheEntry | null> }>();

export function cityImageCacheKey(city: string, country?: string, prompts?: string[]): string {
  const key = `${(city || "").toLowerCase()}|${(country || "").toLowerCase()}`;
  return prompts && prompts.length > 0 ? `${key}|${prompts.join("|")}` : key;
}

export function cityImagePrompts(entry: any): string[] {
  if (Array.isArray(entry?.unsplash_image_prompts)) return entry.unsplash_image_prompts;
  return entry?.unsplash_image_prompt ? [entry.unsplash_image_prompt] : [];
}

export function getCityImageCache(key: string): CityImageCacheEntry | undefined {
  return cache.get(key);
}

export function setCityImageCache(key: string, entry: CityImageCacheEntry): void {
  cache.set(key, entry);
}

export function hasCityImageCache(key: string): boolean {
  return cache.has(key);
}

function toCacheEntry(response: any): CityImageCacheEntry | null {
  const rawUrls = response?.data?.images;
  const rawAttr = response?.data?.attributions;
  if (!Array.isArray(rawUrls)) return null;
  const urls: string[] = [];
  const attributions: (Attribution | null)[] = [];
  rawUrls.forEach((url: unknown, i: number) => {
    if (typeof url !== "string" || !url || url.includes("error.error")) return;
    urls.push(url);
    attributions.push(Array.isArray(rawAttr) ? rawAttr[i] ?? null : null);
  });
  return urls.length > 0 ? { urls, attributions } : null;
}

export function isLoadingCityImages(key: string, count: number): boolean {
  return (loading.get(key)?.count ?? 0) >= count;
}

export function loadCityImages(
  key: string,
  count: number,
  request: () => Promise<unknown>
): Promise<CityImageCacheEntry | null> {
  const running = loading.get(key);
  if (running && running.count >= count) return running.promise;
  const promise: Promise<CityImageCacheEntry | null> = request()
    .then(toCacheEntry)
    .then((entry) => {
      if (entry) setCityImageCache(key, entry);
      return entry;
    })
    .finally(() => {
      if (loading.get(key)?.promise === promise) loading.delete(key);
    });
  loading.set(key, { count, promise });
  return promise;
}
